import { PUCK_CONFIG } from "../constants";
import { useEffect } from "react";
import { CustomField } from "@measured/puck";
import { t } from "i18next";
import { Select, MenuItem, FormControl, Typography, useTheme, Stack } from "@mui/material";
import { usePuckCompanyScore } from "../contexts/PuckCompanyScoreContext";
import { withContexts } from "../wrappers/withContexts";
import NativePuckTitleField from "./NativePuckTitleField";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";

type ScoreSelectFieldProps = {
    field: CustomField<number | undefined>;
    name: string;
    id: string;
    value: number | null;
    onChange: (value: number | undefined) => void;
};

export const ScoreSelectField = (props: ScoreSelectFieldProps) => {
    const { value, onChange } = props;

    const { scores = [] } = useFoodpilotContext();
    const companyContext = usePuckCompanyScore();
    const { companyPost } = companyContext;
    const theme = useTheme();

    const puckSelectStyles = {
        fontFamily: PUCK_CONFIG.FontFamily,
        fontSize: "0.875rem",

        "& .MuiSelect-select": {
            padding: theme.spacing(1.5, 2),
        },

        "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.custom.grey[700]} !important`,
        },
    };

    const scoresParams = useOlapScoresParams({});

    const scoreListSelect = Object.entries(companyPost?.olapScores ?? {}).flatMap(([scoreId, score]) => {
        if (!score.label || score.label.trim() === "") {
            return [];
        }

        return [
            {
                id: Number(scoreId),
                label: scores.find((s) => s.id === Number(scoreId))?.title || score.label,
                unit: score.unit,
                onClick: () => {
                    scoresParams.state.selectVariation(Number(scoreId), null);
                },
            },
        ];
    });

    useEffect(() => {
        if (value === null && scoreListSelect.length > 0 && onChange) {
            onChange(scoreListSelect[0].id);
        }
    }, [scoreListSelect, value, onChange]);

    if (!companyContext) {
        return <></>;
    }

    if (scoreListSelect.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("scores.unavailable", "No scores are available")}
            </Typography>
        );
    }

    return (
        <FormControl fullWidth>
            <NativePuckTitleField label={t("score.title", "Score")} icon="scoresIndicators" />

            <Select
                id={`score-select`}
                value={value || ""}
                displayEmpty
                onChange={(e) => onChange(Number(e.target.value))}
                sx={puckSelectStyles}
            >
                {scoreListSelect.length === 0 && (
                    <MenuItem value="" sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                        <span>{t("score.none", "Select a score")}</span>
                    </MenuItem>
                )}
                {scoreListSelect.map((score) => (
                    <MenuItem key={score.id} value={score.id} sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                        <Stack sx={{ width: "100%" }} spacing={0}>
                            <Typography sx={{ fontFamily: PUCK_CONFIG.FontFamily, fontSize: "0.875rem" }}>
                                {score.label}
                            </Typography>
                            {score.unit && score.unit.trim() !== "" && (
                                <Typography
                                    sx={{
                                        fontFamily: PUCK_CONFIG.FontFamily,
                                        fontSize: "0.75rem",
                                        color: theme.custom.grey[1300],
                                    }}
                                >
                                    {score.unit}
                                </Typography>
                            )}
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default withContexts(ScoreSelectField);
